import angular from "angular";

import {
  Ajax,
  calcReportType,
  Calculation,
  Calculations,
  Language,
  Reports,
  ReportType,
  Objects,
  Translations,
  Texts,
  Dates,
  irepr,
} from "@salaxy/core";
import { CostCenterReportConfigGenerator, IrRowsReportConfigGenerator, RowsReportConfigGenerator, TotalsReportConfigGenerator } from "@salaxy/reports";
import { ColumnDefinition } from "tabulator-tables";
import { SxyTableController, SxyTableOptions } from "../sxy-table/SxyTableController";
import {
  SessionService,
  UiHelpers,
  ExcelHelpers
} from "../../services";
import { CalcReportController } from "./CalcReportController";
import { CalcsIrRowsController } from "./CalcsIrRowsController";

/**
 * Displays the reports for the given calculations.
 */
export class CalcReportsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "AjaxNg1",
    "UiHelpers",
    "SessionService",
    "$timeout",
    "$http",
    "Reports",
    "Calculations"
  ];

  /** Calculations for the reports. */
  public calcs: Calculation[] = [];
  /** Current selected language from UI. */
  public reportLang = Language.Default;

  /** Ref to used CalcReports */
  public $calcReports: CalcReportController;

  /** Ref to used SxyTable */
  public $sxyTable: SxyTableController;

  /** Ref to used CalcsIrRows */
  public $calcsIrRows: CalcsIrRowsController;

  /**
   * The current view of the report. Partial is the default.
   * Please note that not all reports support "page1" or "full".
   */
  public view: "partial" | "page1" | "full";

  /** If true, adds the preview watermark to the preview. */
  public preview?: boolean;

  /**
   * Function that is called when the report type has been changed
   * Function has the following locals:  reportType: the selected report type.
   */
  public onReportTypeChange: (params: {
    /** Selected reportType */
    reportType: ReportType | calcReportType,
  }) => void;

  /** Report type */
  private _reportType: ReportType | calcReportType;

  private employerLang: Language;

  private workerLangs: { [key: string]: Language } = {};

  /** Report templates and the currently selected template */
  private reports = {
    salarySlip: {
      full: "salarySlipV2",
      page1: "salarySlipV2Page1",
      partial: "workerCalculationTablesV2",
    },
    employerReport: {
      full: "employerReportV2",
      page1: "employerReportV2Page1",
      partial: "employerCalculationTablesV2",
    },
    paymentReport: {
      full: "paymentReportV2",
      page1: "paymentReportV2",
      partial: "paymentTablesV2",
    },
    paymentSummaryReport: {
      full: "paymentSummaryReportV2",
      page1: "paymentSummaryReportV2",
      partial: "paymentTablesV2",
    },
    totalsReport: {
      full: "totalsReport",
      page1: "totalsReport",
      partial: "totalsTables",
    },
  };

  private sxyTableConfig: { currentCalc: Calculation | Calculation[], reportLang: Language, config: { options: SxyTableOptions, include: ColumnDefinition[], data: any[] } } = {
    currentCalc: null,
    reportLang: Language.Default,
    config: null
  };

  private _epr = {
    loading: {},
    pending: {},
    latestCalc: {},
    current: {}
  };

  /**
   * Creates a new CalcReportsController
   * @ignore
   */
  constructor(
    private ajax: Ajax,
    private uiHelpers: UiHelpers,
    private sessionService: SessionService,
    private $timeout: angular.ITimeoutService,
    private $http: angular.IHttpService,
    private reportsApi: Reports,
    private calculationsApi: Calculations
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.view = this.view || "partial";
    this.sessionService.getReportLang().then((lang) => {
      this.reportLang = lang;
    });
  }

  /** Sets reportLang and refreshes table if $sxyTable exists */
  public async setReportLang(lang: Language) {
    this.reportLang = lang;
    switch (this.reportType) {
      case ReportType.TotalsReport: {
        if (this.$calcReports) {
          await this.$calcReports.refreshTableConfig();
        }
      }
        break;
      case "rowsReport": {
        if (this.$sxyTable) {
          await this.refreshTableConfig();
        }
      }
        break;
      case "irRows": {
        if (this.$calcsIrRows) {
          await this.$calcsIrRows.refreshTableConfig();
        }
      }
        break;
      case "costCenterReport" as any: {
        if (this.$sxyTable) {
          await this.refreshTableConfig();
        }
        break;
      }
    }
  }

  /**
   * Get lang for employer reports
   * @returns language for calculation
   */
  public getEmployerLang(): Language {
    if (this.reportLang !== Language.Default) {
      return this.reportLang;
    }
    else {
      if (!this.employerLang) {
        this.employerLang = Language.Default;
        this.sessionService.getReportLang().then(lang => {
          this.employerLang = lang;
          // Trigger AngularJs to redraw
          this.$timeout();
        });
      }
    }
    return this.employerLang;
  }

  /**
   * Get lang for worker reports
   * @param calc selected calculation
   * @returns language for calculation
   */
  public getWorkerLang(calc: Calculation): Language {
    if (this.reportLang !== Language.Default) {
      return this.reportLang;
    }
    if (!this.workerLangs[calc.worker.accountId]) {
      this.workerLangs[calc.worker.accountId] = Language.Default;
      this.sessionService.getReportLang(calc.worker.accountId).then(lang => {
        this.workerLangs[calc.worker.accountId] = lang;
        // Trigger AngularJs to redraw
        this.$timeout();
      });
    }

    return this.workerLangs[calc.worker.accountId];
  }

  /** The type for the calculation report. */
  public get reportType(): ReportType | calcReportType {
    return this._reportType;
  }

  /** The type for the calculation report. */
  public set reportType(value: ReportType | calcReportType) {
    if (value != this._reportType) {
      this._reportType = value;
      if (this.onReportTypeChange) {
        this.onReportTypeChange({ reportType: value });
      }
    }
  }

  /**
   * Return languge text to ui
   */
  public get languageText() {
    switch (this.reportLang) {
      case Language.Fi:
        return "SALAXY.ENUM.Language.fi.label";
      case Language.En:
        return "SALAXY.ENUM.Language.en.label";
      case Language.Sv:
        return "SALAXY.ENUM.Language.sv.label";
      case Language.Default:
        return (this.reportType === "salarySlip" || this.reportType === "employerReport" || this.reportType === "paymentReport") ? "SALAXY.NG1.CalcReportsComponent.reportLanguageWorker" : "SALAXY.NG1.CalcReportsComponent.reportLanguageCompany";
    }
  }

  /**
   * Exports tabulator based reports data as excel file
   */
  public async downloadExcel() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    const getExportData = async (data: Calculation[]) => {
      const renderingLang = this.getEmployerLang() ?? Language.Fi;
      await Translations.loadLanguage(renderingLang);
      switch (this.reportType) {
        case "totalsReport": {
          return TotalsReportConfigGenerator.getTableData(data, renderingLang);
        }
        case "rowsReport": {
          return RowsReportConfigGenerator.getTableData(data, renderingLang);
        }
        case "irRows": {
          return IrRowsReportConfigGenerator.getTableData(data, renderingLang);
        }
      }
    };
    const fileNames = {
      "totalsReport": "SALAXY.ENUM.ReportType.totalsReport.label",
      "rowsReport": "SALAXY.NG1.CalcReportsComponent.rowsReport",
      "irRows": "SALAXY.NG1.CalcReportsComponent.irRows"
    };
    const getFileName = (): string => {
      return Texts.escapeFileName(`${Translations.get(fileNames[this.reportType])}_${Dates.format("today", "YYYY_MM")}`);
    };

    ExcelHelpers.export(await getExportData(this.calcs), getFileName());
    loading.dismiss();
  }

  /**
   * Shows a download dialog for the given calculations.
   * @param inline - If true, the download link opens the pdf into a new web page.
   */
  public showDownloadDialog(inline = false) {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    this.reportsApi.getCalculationReport(this.calcs, this.reportType as calcReportType, this.getEmployerLang(), this.reportLang).then(response => {
      if (inline) {
        this.uiHelpers.openFile(response.data as any);
      } else {
        this.uiHelpers.downloadFile(response.data as any, decodeURIComponent(response.headers["content-disposition"]).match(/filename\s*=\s*"?(?<file>.*?)(?:\s*;|"|\s*$)/)?.groups?.file);
      }
      loading.dismiss();
    });
  }

  /** Reloads the current report */
  public reload() {
    const reportType = this.reportType;
    this.reportType = null;
    this.$timeout(0).then(() => {
      this.reportType = reportType;
    });
  }

  /** Loads epr for the calculation */
  public loadEpr(calc: Calculation): irepr.EarningsPayment {
    this.checkForEprChange(calc);
    if (this._epr.pending[calc.id]) {
      if (!this._epr.loading[calc.id]) {
        this._epr.loading[calc.id] = true;
        const load = () => {
          this._epr.pending[calc.id] = false;
          return this.calculationsApi.convertToEpr(calc).then((epr) => {
            this._epr.current[calc.id] = this._epr.current[calc.id] ?? {};
            angular.copy(epr, this._epr.current[calc.id]);
            return this._epr.current[calc.id];
          });
        };

        load().then(() => {
          if (this._epr.pending[calc.id]) {
            load();
          }
          else {
            this._epr.loading[calc.id] = false;
          }
        });
      }
    }
    return this._epr.current[calc.id];
  }

  /**
   * Config for SxyTable
   */
  public get reportTableConfig(): { options: SxyTableOptions, include: ColumnDefinition[], data: any[] } {
    if (!this.calcs) {
      this.sxyTableConfig = {
        currentCalc: null,
        reportLang: null,
        config: null
      };
    } else {
      const calcs = angular.copy(this.calcs);
      if (!Objects.equal(this.sxyTableConfig.currentCalc, calcs) || this.reportLang !== this.sxyTableConfig.reportLang) {
        this.sxyTableConfig.currentCalc = calcs;
        this.sxyTableConfig.reportLang = this.reportLang;
        this.getDefaultLanguage(this.reportLang).then((lang) => {
          this.reportLang = lang;
          this.refreshTableConfig();
        });
      }
    }
    return this.sxyTableConfig.config;
  }

  private async refreshTableConfig() {
    if (this.reportLang == Language.Default) {
      this.reportLang = await this.sessionService.getReportLang();
    }
    const renderingLang = this.reportLang ?? Language.Fi;
    await Translations.loadLanguage(renderingLang);
    switch (this.reportType) {
      case "rowsReport":
        this.sxyTableConfig.config = RowsReportConfigGenerator.getConfig(this.calcs, renderingLang);
        break;
      case "costCenterReport" as any:
        this.sxyTableConfig.config = CostCenterReportConfigGenerator.getConfig(this.calcs, this.sessionService.session?.settings?.costAccounting?.dimensions);
        break;
    }
    await this.$timeout();
    this.$sxyTable?.refresh();
  }

  private async getDefaultLanguage(lang: Language): Promise<Language> {
    if (!lang) {
      return this.sessionService.getReportLang();
    }
    return Promise.resolve(lang);
  }
  private checkForEprChange(calc: Calculation) {
    if (!calc?.id) {
      return false;
    }
    if (!angular.equals(calc, this._epr.latestCalc[calc.id])) {
      this._epr.latestCalc[calc.id] = angular.copy(calc);
      this._epr.pending[calc.id] = true;
    }
  }
}
